import { createSignal, onCleanup, onMount, For, Show } from 'solid-js';
import { createStore, reconcile } from 'solid-js/store';
import { useApi, isOk } from '../api';
import { useSFetch } from '../hooks/sfetch';
import { useToast } from '../components/MessageBox';
import { showConfirm } from '../components/Dialog';
import Text from '../components/Text';
import { EmptyState } from '../components/EmptyState';
import { useTranslation } from '../hooks/useTranslation';
import { firstError } from '../api/envelope';

interface DiagState {
    capturing: boolean;
    captureId: string | null;
    durationMs: number;
    serverSampleCount: number;
    clientSampleCount: number;
    lastRttMs: number;
}

interface ActivePossession {
    botId: string;
    botUsername: string;
    botUuid: string;
    possessorUserId: string;
    possessorUsername: string;
    possessorMinecraftUuid: string;
    startedAtUtc: string;
    sessionId: string;
    online: boolean;
    canKick: boolean;
}

function formatDuration(startedAtUtc: string, now: number): string {
    const start = new Date(startedAtUtc).getTime();
    let seconds = Math.max(0, Math.floor((now - start) / 1000));
    const h = Math.floor(seconds / 3600);
    seconds -= h * 3600;
    const m = Math.floor(seconds / 60);
    const s = seconds - m * 60;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
}

export default function VektorPossessions() {
    const api = useApi();
    const sfetch = useSFetch();
    const toast = useToast();
    const { t } = useTranslation();

    const [possessions, setPossessions] = createStore<ActivePossession[]>([]);
    const [loading, setLoading] = createSignal(true);
    const [kickingBot, setKickingBot] = createSignal<string | null>(null);
    const [diag, setDiag] = createStore<Record<string, DiagState>>({});
    const [now, setNow] = createSignal(Date.now());

    const diagBase = (botId: string) => `/api/vektor/admin/possessions/${botId}/diagnostics`;

    async function fetchPossessions() {
        const res = await api.vektorAdmin.listPossessions();
        if (!isOk(res.data)) {
            toast?.error(firstError(res.data) ?? t('STATE_ERROR_GENERIC'));
            setLoading(false);
            return;
        }
        const rows = (res.data.value.possessions ?? []) as ActivePossession[];
        setPossessions(reconcile(rows, { key: 'sessionId' }));
        setLoading(false);
        for (const p of rows) if (p.canKick) void refreshDiag(p.botId);
    }

    async function refreshDiag(botId: string) {
        const res = await sfetch(`${diagBase(botId)}/status`);
        if (res.ok && res.value) setDiag(botId, res.value as DiagState);
    }

    async function handleStartDiag(p: ActivePossession) {
        const res = await sfetch(`${diagBase(p.botId)}/start`, { method: 'POST' });
        if (!res.ok) {
            toast?.error(firstError(res) ?? t('VEKTOR_DIAGNOSTICS_START_FAILED'));
            return;
        }
        toast?.success(t('VEKTOR_DIAGNOSTICS_STARTED', p.botUsername));
        await refreshDiag(p.botId);
    }

    async function handleStopDiag(p: ActivePossession) {
        const res = await sfetch(`${diagBase(p.botId)}/stop`, { method: 'POST' });
        if (!res.ok) {
            toast?.error(firstError(res) ?? t('VEKTOR_DIAGNOSTICS_STOP_FAILED'));
            return;
        }
        toast?.info(t('VEKTOR_DIAGNOSTICS_STOPPED', p.botUsername));
        await refreshDiag(p.botId);
    }

    async function handleExportDiag(p: ActivePossession) {
        const csrf = document.cookie.split(';').map(c => c.trim()).find(c => c.startsWith('token='))?.slice('token='.length);
        const res = await fetch(`${diagBase(p.botId)}/export`, {
            credentials: 'include',
            headers: csrf ? { 'X-CSRF-Token': csrf } : undefined,
        });
        if (!res.ok) {
            toast?.error(t('STATE_ERROR_GENERIC'));
            return;
        }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `goggles-diagnostics-${p.botUsername}.json`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    }

    async function handleKick(p: ActivePossession) {
        const confirmed = await showConfirm(
            t('VEKTOR_POSSESSIONS_KICK_CONFIRM', p.possessorUsername, p.botUsername),
            true);
        if (!confirmed) return;

        setKickingBot(p.botId);
        const res = await api.vektorAdmin.kick(p.botId);
        setKickingBot(null);

        if (!isOk(res.data)) {
            toast?.error(firstError(res.data) ?? t('STATE_ERROR_GENERIC'));
            return;
        }
        toast?.success(t('VEKTOR_POSSESSIONS_KICKED', p.possessorUsername));
        await fetchPossessions();
    }

    onMount(() => {
        void fetchPossessions();

        const refreshHandle = window.setInterval(() => {
            if (document.visibilityState === 'visible') void fetchPossessions();
        }, 3000);

        const durationHandle = window.setInterval(() => {
            if (document.visibilityState === 'visible') setNow(Date.now());
        }, 1000);

        onCleanup(() => {
            clearInterval(refreshHandle);
            clearInterval(durationHandle);
        });
    });

    return (
        <div class="v" style={{ 'max-width': '1100px', margin: '0 auto', padding: 'var(--spacing-xl)', gap: 'var(--spacing-lg)' }}>
            <div class="h" style={{ 'align-items': 'center', 'justify-content': 'space-between' }}>
                <h1 style={{ margin: 0 }}><Text val="VEKTOR_POSSESSIONS_TITLE"/></h1>
                <span style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-sm)' }}>
                    {possessions.length} <Text val="VEKTOR_POSSESSIONS_ACTIVE"/>
                </span>
            </div>

            <Show when={!loading()} fallback={
                <div class="fcard" style={{ 'text-align': 'center', color: 'var(--dark-text-muted)' }}>
                    <Text val="LOADING"/>
                </div>
            }>
                <Show when={possessions.length > 0} fallback={
                    <EmptyState icon="fa-ghost" title={t('VEKTOR_POSSESSIONS_EMPTY')} />
                }>
                    <div class="v" style={{ gap: 'var(--spacing-md)' }}>
                        <For each={possessions}>{(p) => {
                            return (
                                <div class="fcard h" style={{ 'align-items': 'center', gap: 'var(--spacing-lg)' }}>
                                    <img
                                        src={`https://mc-heads.net/avatar/${p.possessorMinecraftUuid}/40`}
                                        alt=""
                                        style={{ 'border-radius': 'var(--radius-md)' }}
                                        width="40" height="40"/>
                                    <div class="v" style={{ flex: '1', gap: 'var(--spacing-xs)', 'min-width': 0 }}>
                                        <div style={{ 'font-weight': 'var(--font-weight-semibold)' }}>
                                            {p.possessorUsername}
                                            <span style={{ color: 'var(--dark-text-muted)', 'font-weight': 'var(--font-weight-medium)' }}>
                                                {' → '}
                                            </span>
                                            {p.botUsername}
                                        </div>
                                        <div style={{ 'font-size': 'var(--font-size-xs)', color: 'var(--dark-text-muted)', 'font-family': 'var(--font-family-mono)' }}>
                                            <Text val="VEKTOR_POSSESSIONS_STARTED"/>: {formatDuration(p.startedAtUtc, now())} <Text val="VEKTOR_POSSESSIONS_AGO"/>
                                        </div>
                                    </div>
                                    <Show when={!p.online}>
                                        <span class="minicard" style={{ background: 'var(--warning-500)', color: 'white', 'font-size': 'var(--font-size-xs)', padding: '2px 10px' }}>
                                            <Text val="VEKTOR_POSSESSIONS_BOT_OFFLINE"/>
                                        </span>
                                    </Show>
                                    <Show when={p.canKick}>
                                        <div class="h" style={{ 'align-items': 'center', gap: 'var(--spacing-sm)' }}>
                                            <Show when={diag[p.botId]?.capturing}>
                                                <span class="minicard" style={{ background: 'var(--primary-600)', color: 'white', 'font-size': 'var(--font-size-xs)', padding: '2px 10px', 'font-family': 'var(--font-family-mono)' }}>
                                                    <i class="fa-solid fa-circle" style={{ 'font-size': '0.6em', color: 'var(--error-500)' }}/>
                                                    {' '}{diag[p.botId]?.clientSampleCount ?? 0}/{diag[p.botId]?.serverSampleCount ?? 0}
                                                    {' · '}{Math.round(diag[p.botId]?.lastRttMs ?? 0)}ms
                                                </span>
                                            </Show>
                                            <Show when={!diag[p.botId]?.capturing} fallback={
                                                <button class="btn btn-warning btn-sm" onClick={() => void handleStopDiag(p)}>
                                                    <i class="fa-solid fa-stop"/> <Text val="VEKTOR_DIAGNOSTICS_STOP"/>
                                                </button>
                                            }>
                                                <button class="btn btn-secondary btn-sm" onClick={() => void handleStartDiag(p)}>
                                                    <i class="fa-solid fa-gauge-high"/> <Text val="VEKTOR_DIAGNOSTICS_START"/>
                                                </button>
                                            </Show>
                                            <Show when={diag[p.botId]?.captureId}>
                                                <button class="btn btn-secondary btn-sm" onClick={() => handleExportDiag(p)} title={t('VEKTOR_DIAGNOSTICS_EXPORT')}>
                                                    <i class="fa-solid fa-download"/>
                                                </button>
                                            </Show>
                                        </div>
                                    </Show>
                                    <Show when={p.canKick}>
                                        <button
                                            class="btn btn-danger"
                                            disabled={kickingBot() === p.botId}
                                            onClick={() => void handleKick(p)}>
                                            <i class="fa-solid fa-hand"/> <Text val="VEKTOR_POSSESSIONS_KICK"/>
                                        </button>
                                    </Show>
                                </div>
                            );
                        }}</For>
                    </div>
                </Show>
            </Show>
        </div>
    );
}
